import bookaMeetingApi from './request';

export type LeadFormMessage = {
  at: string;
  text: string;
};

export type BusinessLead = {
  conversationId: string;
  name: string;
  whatsappNumber: string;
  instagramId: string;
  instagramCustomerId: string;
  updatedAt: string;
  connection: string;
  formFilled?: string;
  forms?: LeadFormMessage[];
  stageId?: string;
  tagIds?: string[];
};

/** People saved on this business. Does not load their messages. */
export async function getBusinessLeads(input: {
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
}): Promise<BusinessLead[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    leads?: BusinessLead[];
  }>({
    url: '/leads',
    method: 'GET',
    params: {
      ...(input.whatsappBusinessId ? { whatsappBusinessId: input.whatsappBusinessId } : {}),
      ...(input.instagramBusinessId ? { instagramBusinessId: input.instagramBusinessId } : {}),
    },
  });
  return response.data?.leads || [];
}

export type LeadPipeline = {
  stages: Array<{
    id: string;
    name: string;
    color: string;
    description: string;
    aiOn: boolean;
    requireAttribute: boolean;
    attributeName: string;
  }>;
  tags: Array<{ id: string; name: string; color: string }>;
  fields: Array<{ id: string; name: string }>;
};

/** Stages, tags, and extra columns saved for this business. */
export async function getLeadPipeline(businessId: string): Promise<LeadPipeline | null> {
  if (!businessId) return null;
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    stages?: LeadPipeline['stages'];
    tags?: LeadPipeline['tags'];
    fields?: LeadPipeline['fields'];
  }>({
    url: '/leads/pipeline',
    method: 'GET',
    params: { businessId },
  });
  if (!response.data?.success) return null;
  return {
    stages: response.data.stages || [],
    tags: response.data.tags || [],
    fields: response.data.fields || [],
  };
}

/** Saves stages, tags, and extra columns for this business. */
export async function saveLeadPipeline(input: LeadPipeline & { businessId: string }): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/pipeline',
    method: 'PUT',
    data: input,
  });
}

export async function createBusinessLead(input: {
  businessId: string;
  instagramBusinessId?: string;
  name: string;
  whatsappNumber?: string;
  instagramId?: string;
}): Promise<{ success?: boolean; conversationId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    conversationId?: string;
    error?: string;
  }>({
    url: '/leads',
    method: 'POST',
    data: input,
  });
  return response.data;
}

export type ConnectLeadInput = {
  whatsappBusinessId: string;
  instagramBusinessId: string;
  whatsappNumber: string;
  instagramId: string;
  name: string;
};

export type ConnectLeadResult = {
  success?: boolean;
  conversationId?: string;
  personId?: string;
  error?: string;
};

/** Tell the lead table these two channels are one person for this business. */
export async function connectBusinessLead(input: ConnectLeadInput): Promise<ConnectLeadResult> {
  if (!input.whatsappNumber && !input.instagramId) return {};
  if (!input.whatsappBusinessId && !input.instagramBusinessId) return {};
  const response = await bookaMeetingApi.requestWIthAuth<ConnectLeadResult>({
    url: '/leads/connect',
    method: 'POST',
    data: input,
  });
  return response.data;
}

export type WebappEventInput = {
  businessId: string;
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
  whatsappNumber?: string;
  instagramId?: string;
  conversationId?: string;
  type: 'message' | 'form' | 'event';
  name: string;
  text?: string;
  fields?: Record<string, string>;
};

/** Save a web app message, form fill, or event on the shared conversation. */
export async function saveWebappEvent(
  input: WebappEventInput
): Promise<{ success?: boolean; conversationId?: string; mid?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    conversationId?: string;
    mid?: string;
    error?: string;
  }>({
    url: '/webapp/events',
    method: 'POST',
    data: input,
  });
  return response.data;
}
